import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, Barcode as BarcodeIcon } from '@phosphor-icons/react';
import { Button } from './ui/button';

export default function BarcodeScanner({ onScan, onClose, title = 'Scan Barcode' }) {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [error, setError] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [cameraReady, setCameraReady] = useState(false);

  useEffect(() => {
    const startScanner = async () => {
      try {
        const qrCode = new Html5Qrcode('scanner-region');
        html5QrCodeRef.current = qrCode;
        const devices = await Html5Qrcode.getCameras();
        if (!devices || devices.length === 0) {
          setError('No camera found. Please use manual input.');
          setManualMode(true);
          return;
        }
        const cameraId = devices.find(d => /back|rear|environment/i.test(d.label))?.id || devices[0].id;
        await qrCode.start(
          cameraId,
          { fps: 10, qrbox: { width: 280, height: 140 }, aspectRatio: 1.777 },
          (decodedText) => { onScan(decodedText); stopScanner(); },
          () => {}
        );
        setCameraReady(true);
      } catch (err) {
        console.error('Scanner error:', err);
        setError('Camera access denied. Use manual input below.');
        setManualMode(true);
      }
    };
    if (!manualMode) startScanner();
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualMode]);

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (e) {}
      html5QrCodeRef.current = null;
    }
  };

  const handleClose = async () => { await stopScanner(); onClose(); };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) onScan(manualInput.trim());
  };

  return createPortal((
    <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col" data-testid="barcode-scanner">
      <div className="bg-black text-white p-4 flex items-center justify-between border-b border-slate-800">
        <div className="flex items-center gap-2">
          <BarcodeIcon size={24} weight="bold" />
          <h2 className="text-lg font-bold">{title}</h2>
        </div>
        <button onClick={handleClose} data-testid="scanner-close" className="p-2 hover:bg-slate-800">
          <X size={24} weight="bold" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center relative overflow-hidden bg-black">
        {!manualMode && (
          <>
            <div id="scanner-region" ref={scannerRef} className="w-full max-w-lg" />
            {cameraReady && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="relative w-[280px] h-[140px]">
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-red-500"></div>
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-red-500"></div>
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-red-500"></div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-red-500"></div>
                  <div className="absolute inset-x-0 h-[2px] bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.9)] scan-line"></div>
                </div>
              </div>
            )}
          </>
        )}
        {error && (
          <div className="absolute top-4 left-4 right-4 bg-red-600 text-white p-3 text-center text-sm">{error}</div>
        )}
      </div>

      <div className="bg-card p-4 border-t border-border">
        {!manualMode ? (
          <Button onClick={() => setManualMode(true)} variant="outline" className="w-full h-12 font-bold" data-testid="scanner-manual-toggle">
            <Camera size={20} className="mr-2" /> Enter Manually Instead
          </Button>
        ) : (
          <form onSubmit={handleManualSubmit} className="space-y-3">
            <label className="overline block">Manual Barcode Entry</label>
            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder="Enter or paste barcode..."
              autoFocus
              className="w-full h-14 px-4 border border-border font-mono text-lg focus:outline-none focus:border-brand"
              data-testid="scanner-manual-input"
            />
            <Button type="submit" className="w-full h-14 bg-brand hover:bg-brand/90 font-bold text-lg" data-testid="scanner-manual-submit">
              Submit Barcode
            </Button>
          </form>
        )}
      </div>
    </div>
  ), document.body);
}
